import { CheckCircleIcon, InfoIcon, QuestionMarkIcon, TimeCircleIcon } from '@systemeio/ui/icons'
import { FormInput, FormInputSearch, Popover, RadioGroup, RadioGroupDataInterface } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import MailingStatsSkeleton from 'modules/campaigns/campaign-steps/components/mailing-stats-skeleton'
import EmailTemplateSelector from 'modules/email/components/email-template-selector/email-template-selector'
import { useGetEmailTemplates } from 'modules/email/hooks/use-get-email-templates'
import { getEmailStatisticsUrl } from 'modules/mailing/email-statistics/utils/get-email-statistics-url'
import { CreateEmailInterface } from 'modules/mailing/types/mailing-interface'
import {
  NEWSLETTERS_API,
  createNewsletter,
  duplicateNewsletter,
  removeNewsletter,
  resendNewsletter,
} from 'modules/newsletters/single/api/newsletters-api'
import ResendNewsletterModalWithoutButton from 'modules/newsletters/single/components/resend-newsletter-modal/resend-newsletter-modal-without-buttons'
import { getNewsletterSingleColumnTitle } from 'modules/newsletters/single/configs/newsletter-single-columns'
import { NewslettersLoco } from 'modules/newsletters/single/constants/newsletters-loco'
import { useNewsLetters } from 'modules/newsletters/single/hooks/use-newsletter'
import {
  NewsletterCreateErrorsInterface,
  NewsletterErrorResponseContentInterface,
} from 'modules/newsletters/single/types/error-response-content-interface'
import { NewsletterFilterInterface } from 'modules/newsletters/single/types/newsletter-filter-interface'
import { NewsletterId, NewsletterType } from 'modules/newsletters/single/types/newsletter-interface'
import { getNewslettersEditorNextjsUrl } from 'modules/newsletters/single/utils/get-newsletters-editor-nextjs-url'
import { InterruptSplitTest } from 'modules/newsletters/split-test/components/interrupt-split-test'
import {
  getNewsletterSplitTestColumnTitle,
  getNewsletterSplitTestStatus,
} from 'modules/newsletters/split-test/configs/newsletter-split-test-columns'
import { MailingSplitTestStatusTypeEnum } from 'modules/newsletters/split-test/enum/mailing-split-test-status-type-enum'
import { getSplitTestNewsletterStatisticsUrl } from 'modules/newsletters/split-test/utils/get-split-test-newsletter-statistics-url'
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum'
import { getShareUrl } from 'modules/share/utils/get-share-url'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import { CreateDataType } from 'shared/components/create-modal'
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options'
import LinkShareModal from 'shared/components/link-share-modal'
import ListingLayout from 'shared/components/listing-layout'
import SenderEmailSelect from 'shared/components/sender-email-select'
import { ListOfItemsCell } from 'shared/components/table/cells/list-of-items-cell'
import { TextWithTooltipCell } from 'shared/components/table/cells/text-with-tooltip-cell'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { isValidEmail } from 'shared/utils/email-validation'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getPercentAndAmountStatistic } from 'shared/utils/get-percent-and-amount-statistic'

const defaultValues: CreateEmailInterface = {
  subject: '',
  fromName: '',
  fromEmail: '',
  useNewEditor: true,
  template: undefined,
}

const defaultErrors = {
  subject: '',
  fromName: '',
  fromEmail: '',
}
const defaultFilters: NewsletterFilterInterface = { search: '' }

const Newsletter: NextPage = () => {
  const { user } = useUser()
  const router = useRouter()
  const { setPagination, pagination, setLimit, limit, filter, setFilter } =
    useFilterPagination(defaultFilters)

  const updateData = useUpdateData({
    api: NEWSLETTERS_API,
    constantApi: [`${NEWSLETTERS_API}/templates`],
    setPagination,
  })
  const [errors, setErrors] = useState<NewsletterCreateErrorsInterface>(defaultErrors)
  const [preLoadedValues, setPreLoadedValues] = useState<CreateEmailInterface>(defaultValues)

  const { data: emailOptions } = useEmailOptions()

  const { templates } = useGetEmailTemplates()

  useEffect(() => {
    if (templates) {
      setPreLoadedValues(prev => ({
        ...prev,
        template: templates?.[0].id,
      }))
    }
  }, [templates])

  useEffect(() => {
    if (emailOptions) {
      setPreLoadedValues(prev => ({
        ...prev,
        fromEmail: prev.fromEmail || emailOptions.senderEmailAddress,
        fromName: prev.fromName || emailOptions.senderName,
      }))
    }
  }, [emailOptions])

  const { t } = useLocoTranslation()

  const { newsletters, counter, mutate } = useNewsLetters({ limit, pagination, filter })

  const columns: ColumnDefinitionInterface<NewsletterType>[] = [
    {
      key: 'title',
      header: t('dashboard.newsletter.newsletter_title'),
      widthPercent: 35,
      onRender: newsletter => (
        <>
          {newsletter.mailingSplitTest
            ? getNewsletterSplitTestColumnTitle(newsletter, t)
            : getNewsletterSingleColumnTitle(newsletter, t)}
        </>
      ),
    },
    {
      key: 'connectedTags',
      header: t('dashboard.newsletter.connected_tags'),
      widthPercent: 20,
      onRender: newsletter => (
        <ListOfItemsCell
          items={newsletter.tags.map(({ name }) => name)}
          entityName={t('dashboard.tag.title_plural')}
        />
      ),
    },
    {
      key: 'emailsSentCount',
      header: (
        <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.emails_sent_count')}
          </TextWithTooltipCell>
        </div>
      ),
      widthPercent: 10,
      onRender: newsletter => (
        <TextWithTooltipCell wrapperClassName="justify-center">
          {newsletter.stats?.emailsSent}
        </TextWithTooltipCell>
      ),
    },
    {
      key: 'numberOfViews',
      header: (
        <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.number_of_views')}
          </TextWithTooltipCell>
        </div>
      ),
      widthPercent: 10,
      onRender: newsletter =>
        newsletter.stats ? (
          <div className="overflow-hidden lg:w-full">
            <TextWithTooltipCell wrapperClassName="justify-center">
              {getPercentAndAmountStatistic({
                numberOfActions: newsletter.stats.emailsOpened,
                totalNumber: newsletter.stats.emailsSent,
              })}
            </TextWithTooltipCell>
          </div>
        ) : (
          <MailingStatsSkeleton />
        ),
    },
    {
      key: 'numberOfClicks',
      header: (
        <div className="overflow-hidden lg:w-full">
          <TextWithTooltipCell wrapperClassName="justify-center">
            {t('dashboard.newsletter.clicks_count')}
          </TextWithTooltipCell>
        </div>
      ),
      widthPercent: 10,
      onRender: newsletter =>
        newsletter.stats ? (
          <div className="overflow-hidden lg:w-full">
            <TextWithTooltipCell wrapperClassName="justify-center">
              {getPercentAndAmountStatistic({
                numberOfActions: newsletter.stats.clicks,
                totalNumber: newsletter.stats.emailsSent,
              })}
            </TextWithTooltipCell>
          </div>
        ) : (
          <MailingStatsSkeleton />
        ),
    },
    {
      key: 'status',
      header: t('global.status'),
      widthPercent: 10,
      onRender: newsletter => {
        return (
          <>
            {newsletter.scheduledAt === null ? (
              ''
            ) : (
              <>
                {newsletter.isPending ? (
                  <div className="lg:ml-4">
                    <Popover
                      label={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: t('dashboard.newsletter.status_pending'),
                          }}
                        />
                      }
                      popoverClassName={'w-[250px] sm:w-[450px]'}
                    >
                      {show => (
                        <InfoIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } fill-fill-primary h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`}
                        />
                      )}
                    </Popover>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {newsletter.sent ? (
                      <>
                        {newsletter.mailingSplitTest ? (
                          getNewsletterSplitTestStatus(newsletter.mailingSplitTest, t)
                        ) : (
                          <CheckCircleIcon
                            className={`fill-green h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`}
                          />
                        )}
                      </>
                    ) : (
                      <TimeCircleIcon
                        className={`fill-yellow h-[16px] w-[16px] shrink-0 sm:h-[20px] sm:w-[20px]`}
                      />
                    )}
                    {dateTimeFormat(newsletter.scheduledAt, {
                      timeFormat: user?.timeFormat,
                      dateFormat: user?.dateFormat,
                      timeZone: user?.timezone,
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )
      },
    },
  ]

  const onDuplicateNewsletter = async (newsletter: NewsletterType) => {
    const response = await duplicateNewsletter(newsletter.id)
    updateData(false, newsletters)
    router.push(getNewslettersEditorNextjsUrl(response.data.id))
  }

  const onCreate = async (data: CreateDataType<CreateEmailInterface>) => {
    try {
      if (!isValidEmail(data.fromEmail)) {
        setErrors(prev => ({
          ...prev,
          fromEmail: t('dashboard.validation.email_not_valid'),
        }))
        throw new Error()
      }
      const newsletterData = {
        mailing: {
          currentEmail: {
            fromEmail: data.fromEmail,
            fromName: data.fromName,
            subject: data.subject,
            useNewEditor: data.useNewEditor,
            template: data.template,
          },
        },
      }
      await createNewsletter(newsletterData)
      await updateData(true, newsletters)
    } catch (e) {
      if (e instanceof BadRequest && e.errors.fields) {
        const errors = e.errors as NewsletterErrorResponseContentInterface
        const currentlMailing = !Array.isArray(errors.fields) ? errors.fields.mailing : undefined
        if (currentlMailing) {
          setErrors({
            subject: currentlMailing?.currentEmail?.subject?.join('') || '',
            fromName: currentlMailing?.currentEmail?.fromName?.join('') || '',
            fromEmail: currentlMailing?.currentEmail?.fromEmail?.join('') || '',
          })
        }
      } else {
        throw e
      }
      throw new Error()
    }
  }

  const onResendNewsletter = (newsletter: NewsletterType) => async (data: any) => {
    await resendNewsletter(newsletter.id, data)
    await updateData(false, newsletters)
  }

  const onRemoveNewsletter = async (newsletter: NewsletterType) => {
    await removeNewsletter(newsletter.id)
    await updateData(false, newsletters)
  }

  const onModalClose = () => setErrors(defaultErrors)

  const interruptSplitTest = async (newsletterId: NewsletterId) => {
    await mutate(
      data =>
        data && {
          hasMore: data.hasMore,
          items: data.items.map(el => {
            if (el.id === newsletterId && el.mailingSplitTest) {
              return {
                ...el,
                mailingSplitTest: {
                  ...el.mailingSplitTest,
                  status: MailingSplitTestStatusTypeEnum.Interrupted,
                },
              }
            } else {
              return el
            }
          }),
        },
      false,
    )
  }

  const templateTypeData: RadioGroupDataInterface<string>[] = [
    { id: 'new', caption: t('dashboard.email_template_selector.template_visual_editor') },
    { id: 'old', caption: t('dashboard.email_template_selector.template_classic_editor') },
  ]

  return (
    <ListingLayout
      counter={counter}
      data={newsletters}
      title={
        <h1 className={'flex items-center gap-1.5'}>
          {t('dashboard.newsletter.newsletters')}
          <Popover
            label={t('header.top_menu.newsletters_tooltip_text')}
            popoverClassName={'w-[200px] sm:w-[350px]'}
          >
            {show => (
              <QuestionMarkIcon
                className={`${
                  show ? 'fill-brand-primary' : 'fill-fill-primary'
                } group-focus-visible:fill-brand-primary`}
              />
            )}
          </Popover>
        </h1>
      }
      type={'compact'}
      filterData={{
        filterer: { filter, setFilter, defaultFilters },
        paginator: { pagination, setPagination },
        limiter: { limit, setLimit },
        filterable: [
          {
            field: 'search',
            onRender: (value, onChange) => (
              <FormInputSearch
                key={'newsletter-search-filter'}
                className={'lg:w-72'}
                placeholder={t('global.search')}
                onClear={() => onChange('')}
                value={value}
                onChange={e => onChange(e.target.value)}
              />
            ),
          },
        ],
      }}
      createData={{
        caption: t('dashboard.newsletter.newsletter'),
        requiredFields: ['subject', 'fromName', 'fromEmail'],
        onAfterLeave: onModalClose,
        onCreate: data => onCreate({ ...preLoadedValues, ...data }),
        defaultValues: preLoadedValues,
        creatable: [
          {
            field: 'subject',
            onRender: (value, onChange) => (
              <FormInput
                required
                key="newsletter-subject"
                label={
                  <div className="flex items-center gap-2">
                    {t('dashboard.newsletter.subject_title')}{' '}
                    <Popover
                      label={
                        <div
                          className="[&>a]:text-brand-primary"
                          dangerouslySetInnerHTML={{
                            __html: t('dashboard.newsletter.subject_title_tooltip'),
                          }}
                        />
                      }
                      popoverClassName={'w-[160px] sm:w-[350px]'}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                placeholder={t('dashboard.newsletter.subject_title')}
                value={value}
                onChange={e => onChange(e.target.value)}
                maxLength={256}
                error={errors.subject}
              />
            ),
          },
          {
            field: 'fromName',
            onRender: (value, onChange) => (
              <FormInput
                required
                key="newsletter-sender-name"
                label={t('dashboard.newsletter.sender_name_title')}
                value={value}
                onChange={e => onChange(e.target.value)}
                maxLength={256}
                error={errors.fromName}
              />
            ),
          },
          {
            field: 'fromEmail',
            onRender: (value, onChange) => (
              <SenderEmailSelect
                required
                key="newsletter-sender-email"
                onChange={data => data && onChange(data)}
                value={value}
                error={errors.fromEmail}
              />
            ),
          },
          {
            field: 'useNewEditor',
            onRender: (value, onChange, state, onChangeState) => (
              <RadioGroup
                key="newsletter-editor-type"
                options={templateTypeData}
                selected={value ? 'new' : 'old'}
                onChange={value => {
                  if (value === 'old') {
                    onChangeState({ ...state, useNewEditor: false, template: undefined })
                  } else
                    onChangeState({ ...state, useNewEditor: true, template: templates?.[0].id })
                }}
              />
            ),
          },
          {
            field: 'template',
            shouldRender: (state: CreateEmailInterface) => state.useNewEditor,
            onRender: (value, onChange) => (
              <EmailTemplateSelector
                key="template"
                onChange={onChange}
                templateId={value}
                templates={templates}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.newsletter.newsletter').toLowerCase()}
        data={newsletters}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          openLink: [
            {
              name: 'edit',
              caption: t('dashboard.actions.title.edit'),
              shouldRender: newsletter => !newsletter.sent,
              setHref: newsletter => getNewslettersEditorNextjsUrl(newsletter.id),
              isNext: true,
              openInNew: false,
            },
            {
              shouldRender: newsletter => newsletter.sent,
              name: 'stats',
              caption: t('dashboard.actions.title.stats'),
              setHref: newsletter =>
                newsletter.mailingSplitTest
                  ? getSplitTestNewsletterStatisticsUrl(newsletter.id)
                  : getEmailStatisticsUrl(newsletter.mailing.id),
              isNext: true,
            },
          ],
          custom: [
            {
              shouldRender: newsletter => newsletter.sent && !newsletter.mailingSplitTest,
              name: 'resend',
              caption: t('dashboard.newsletter.resend_title'),
              onActionRender: (opened, onClose, newsletter, key) => (
                <ResendNewsletterModalWithoutButton
                  opened={opened}
                  subject={newsletter.mailing.currentEmail.subject}
                  onClose={onClose}
                  key={key}
                  onResend={onResendNewsletter(newsletter)}
                />
              ),
            },
            {
              name: 'share',
              caption: t('dashboard.actions.title.share'),
              shouldRender: newsletter => !newsletter.mailingSplitTest,
              onActionRender: (opened, onClose, newsletter, key) => (
                <LinkShareModal
                  opened={opened}
                  link={getShareUrl(ShareResourceEnum.Newsletter, newsletter.hash)}
                  title={t('dashboard.newsletter.share_newsletter')}
                  label={t('dashboard.newsletter.share_newsletter_text')}
                  onClose={onClose}
                  key={key}
                />
              ),
            },
            {
              shouldRender: newsletter =>
                !newsletter.excludeRecipientsOfNewsletter && !newsletter.mailingSplitTest,
              name: 'duplicate',
              caption: t('dashboard.actions.title.duplicate'),
              onActionRender: (opened, onClose, newsletter, key) => (
                <ConfirmationModal
                  confirmationText={t('dashboard.newsletter.duplicate_confirm')}
                  key={key}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => onDuplicateNewsletter(newsletter)}
                  toastCaption={t('global.duplicated')}
                />
              ),
            },
            {
              name: 'interruptSplitTest',
              caption: t('dashboard.newsletter_editor.split_test.interrupt.title'),
              onActionRender: (opened, onClose, newsletter, key) => (
                <InterruptSplitTest
                  mailingSplitTestId={newsletter.mailingSplitTest?.id}
                  opened={opened}
                  onClose={onClose}
                  afterAbandon={() => interruptSplitTest(newsletter.id)}
                  key={key}
                />
              ),
              shouldRender: newsletter =>
                newsletter.mailingSplitTest?.status === MailingSplitTestStatusTypeEnum.Running,
            },
          ],
          remove: {
            shouldRender: newsletter =>
              newsletter.mailingSplitTest?.status !== MailingSplitTestStatusTypeEnum.Running,
            onChange: newsletter => onRemoveNewsletter(newsletter),
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  NewslettersLoco,
  'header.top_menu.newsletters',
)

export default Newsletter
